import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@hooks/useTheme";
import { TableWithPagination } from "../Tables";
import { getIndexDBData, STORES } from "@utils/indexedDb";
import TableHeaderControls from "@components/Project/TableHeaderControls";
import AdvancedSearch from "@components/Search/AdvancedSearch";
import { useAdvancedSearch } from "@hooks/useAdvancedSearch";
import { resolveSearchCriteria } from "@utils/searchHelpers";
import { useSearchInfo } from "@hooks/useSearchInfo";
import { formatDateForRequest, translateDepartment } from "@utils/common";
import useEscapeKey from "@hooks/useEscapeKey";
import { PermissionContext } from "@contexts/PermissionContext";
import { LOCAL_STORAGE } from "@constants";

const SelectVerificationReportVersionModal = ({
  isOpen,
  onClose,
  onSelect,
  selectedVerificationReportSeq,
  managedProjectsOnly = false,
  "data-testid": dataTestId = "select-verification-report-version-modal",
}) => {
  const fieldInputTestId = (name) => `${dataTestId}-field-${name}-input`;
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { hasManagePermission } = useContext(PermissionContext);

  const getManagedProjectFilters = useCallback(() => {
    if (!managedProjectsOnly || hasManagePermission) {
      return {};
    }

    const userId = localStorage.getItem(LOCAL_STORAGE.USER_ID);
    return userId ? { projectManagerId: userId } : {};
  }, [managedProjectsOnly, hasManagePermission]);

  const [verificationReports, setVerificationReports] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);

  const [searchFilters, setSearchFilters] = useState([]);
  const [isVerificationReportListLoading, setIsVerificationReportListLoading] = useState(false);
  const isFirstRender = useRef(true);

  // Search handler
  const searchHandler = useCallback((inputData) => {
    setSearchFilters(prev => ({
      ...prev,
      ...inputData,
    }));
  }, []);

  const {
    departmentList,
    creatorList,
    fetchSearchInfo,
  } = useSearchInfo();

  const searchFields = [
    {
      name: "verificationReportId",
      label: t("project.verification-report.verification-report-id"),
      placeholder: t("project.verification-report.verification-report-id-placeholder"),
    },
    {
      name: "verificationReportName",
      label: t("project.verification-report.verification-report-name"),
      placeholder: t("project.verification-report.verification-report-name-placeholder"),
    },
    {
      name: "createrId",
      label: t("project.verification-report.creator"),
      type: "select",
      placeholder: t("project.verification-report.creator"),
      keepRawLabel: true,
      options: creatorList,
      displayFormatLabelWithID: true,
    },
    // {
    //   name: "createrDeptCd",
    //   label: t("project.verification-report.creator-department"),
    //   type: "select",
    //   placeholder: t("project.verification-report.creator-department"),
    //   keepRawLabel: true,
    //   options: departmentList.map((item) => ({
    //     label: translateDepartment(t, item.value),
    //     value: item.value,
    //   })),
    // },
  ];

  const { showAdvancedSearch, setShowAdvancedSearch, handleAdvancedSearchToggle } = useAdvancedSearch({
    searchFields,
    quickSearchField: "search",
    setSearchCriteria: setSearchFilters,
  });

  // --- RESTORED FETCH LOGIC ---
  useEffect(() => {
    fetchSearchInfo && fetchSearchInfo();
  }, []);

  useEffect(() => {
    fetchData();
  }, [currentPage]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      fetchData();
    }
  }, [searchFilters, itemsPerPage]);

  const getListData = async (bodyRequest = {}) => {
    try {
      const {
        pageSize = itemsPerPage,
        startRow = 0,
        ...filters
      } = bodyRequest;
      const response = await getIndexDBData(STORES.VERIFICATION_REPORTS, {
        pageSize,
        startRow,
        filters: {
          ...getManagedProjectFilters(),
          ...filters,
        },
      });

      if (response && typeof response !== "string") {
        setVerificationReports(response.object || []);
        const totalCount = response.objectCount || 0;
        setTotalData(totalCount);
        setTotalPages(Math.ceil(totalCount / (pageSize || 1)));
        setIsVerificationReportListLoading(true);
      }
    } catch (error) {
      console.error("Error fetching verification reports from IndexedDB:", error);
    } finally {
      setIsVerificationReportListLoading(false);
    }
  };

  const fetchData = () => {
    const effectiveCriteria = resolveSearchCriteria(searchFilters, {
      quickSearchField: "search",
      isAdvancedPanelOpen: showAdvancedSearch,
    });

    const bodyRequest = Object.keys(effectiveCriteria).reduce((acc, key) => {
      let value = effectiveCriteria[key];
      if (typeof value === "string") {
        value = value.trim();
      }
      if (value !== "" && value !== undefined) {
        acc[key] = formatDateForRequest(value);
      }
      return acc;
    }, {});
    bodyRequest["pageSize"] = itemsPerPage;
    bodyRequest["startRow"] = itemsPerPage * (currentPage - 1);
    bodyRequest["verificationReportStatusCd"] = "DISTRIBUTION_DONE";
    getListData(bodyRequest);
  };

  useEffect(() => {
    if (!isOpen) {
      setSearchFilters({});
      setShowAdvancedSearch(false);
      setCurrentPage(1);
      setItemsPerPage(10);
      setSelectedReport(null);
      setVerificationReports([]);
      setTotalData(0);
      setTotalPages(0);
    }
  }, [isOpen]);


  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
      setItemsPerPage(10);
      setIsClosing(false);
    }
  }, [isOpen]);

  // Fetch data when page or itemsPerPage changes
  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [currentPage, itemsPerPage, isOpen]);

  // Set selected report when data is loaded
  useEffect(() => {
    if (isOpen && verificationReports.length > 0) {
      const currentSelectedSeq = selectedReport?.verificationReportSeq;
      
      // If we already have a selected report, verify it's still valid
      if (selectedReport) {
        const isValid = verificationReports.some(
          (r) => r.verificationReportSeq === currentSelectedSeq
        );
        if (isValid) {
          // Selected report is valid, keep it
          return;
        }
        // If not in current page, try to find it in all data
        const fetchAndVerify = async () => {
          try {
            const response = await getIndexDBData(STORES.VERIFICATION_REPORTS, {
              filters: {
                ...getManagedProjectFilters(),
                verificationReportStatusCd: "DISTRIBUTION_DONE",
              },
              pageSize: 10000,
              startRow: 0,
            });
            if (response && typeof response !== "string" && response.object) {
              const found = response.object.find(
                (r) => r.verificationReportSeq === currentSelectedSeq
              );
              if (!found) {
                // Selected report no longer exists, restore from prop
                if (selectedVerificationReportSeq) {
                  const propReport = response.object.find(
                    (r) => r.verificationReportSeq === selectedVerificationReportSeq
                  );
                  if (propReport) setSelectedReport(propReport);
                  else setSelectedReport(null);
                } else {
                  setSelectedReport(null);
                }
              }
            }
          } catch (error) {
            console.error("Error verifying selected report:", error);
          }
        };
        fetchAndVerify();
        return;
      }
      
      // No selected report, restore from prop if available
      if (selectedVerificationReportSeq) {
        const foundReport = verificationReports.find(
          (r) => r.verificationReportSeq === selectedVerificationReportSeq
        );
        if (foundReport) {
          setSelectedReport(foundReport);
        } else {
          // If not found in current page, fetch all to find it
          const fetchSelectedReport = async () => {
            try {
              const response = await getIndexDBData(STORES.VERIFICATION_REPORTS, {
                filters: {
                  ...getManagedProjectFilters(),
                  verificationReportStatusCd: "DISTRIBUTION_DONE",
                },
                pageSize: 10000,
                startRow: 0,
              });
              if (response && typeof response !== "string" && response.object) {
                const foundReport = response.object.find(
                  (r) => r.verificationReportSeq === selectedVerificationReportSeq
                );
                if (foundReport) {
                  setSelectedReport(foundReport);
                }
              }
            } catch (error) {
              console.error("Error fetching selected report:", error);
            }
          };
          fetchSelectedReport();
        }
      }
    }
  }, [isOpen, selectedVerificationReportSeq, verificationReports]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 150);
  };

  useEscapeKey(handleClose, isOpen);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const handleRowClick = (report) => {
    setSelectedReport(report);
  };

  const handleRadioTableChange = (selected) => {
    if (selected && selected.length > 0) {
      const report = verificationReports.find((r) => r.verificationReportSeq === selected[0]);
      setSelectedReport(report);
    } else {
      setSelectedReport(null);
    }
  };

  const handleConfirm = () => {
    if (selectedReport) {
      onSelect(selectedReport);
      handleClose();
    }
  };

  // Status rendering
  const renderStatusCell = (status) => {
    const STATUS = {
        "DISTRIBUTION_COMPLETED": "Distribution Completed",
      }
  
      const statusStyles = {
        [STATUS.DISTRIBUTION_COMPLETED]: colors.textSuccess,
      };
      const statusColorDot = {
        [STATUS.DISTRIBUTION_COMPLETED]: colors.bgStatusSuccess,
      };
      return (
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-sm font-semibold ${statusStyles[status] || `${colors.bgStatusTagNeutral} ${colors.textPrimary}`}`}
        >
          <div className={`mr-2 h-2 w-2 rounded-full ${statusColorDot[status]}`}></div>
          {t(`project.verification-report.verification-report-status-${status?.toLowerCase() || ""}`, status)}
        </span>
      );
  };

  const columns = [
    "verificationReportId",
    "verificationReportName",
    "verificationReportStatusCdName",
    "verificationVersion",
    // "createrDeptCdName",
    "createrName",
  ];

  const columnConfig = {
    verificationReportId: {
      headerRender: () => t("project.verification-report.verification-report-id"),
      style: { width: "18%" },
    },
    verificationReportName: {
      headerRender: () => t("project.verification-report.verification-report-name"),
      style: { width: "30%" },
    },
    verificationReportStatusCdName: {
      headerRender: () => t("project.verification-report.verification-report-status"),
      customRender: (value, row) => renderStatusCell(value || row.verificationReportStatusCdName),
      style: { width: "22%", align: "left" },
    },
    verificationVersion: {
      headerRender: () => t("project.verification-report.verification-version"),
      style: { width: "12%" },
    },
    // createrDeptCdName: {
    //   headerRender: () => t("project.verification-report.creator-department"),
    //   customRender: (value) => <div>{translateDepartment(t, value) || "-"}</div>,
    //   style: { width: "14%" },
    // },
    createrName: {
      headerRender: () => t("project.verification-report.creator"),
      customRender: (value) => <div>{value || "-"}</div>,
      style: { width: "14%" },
    },
  };

  if (!isOpen) return null;

  return (
    <div
      data-testid={`${dataTestId}-overlay`}
      className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex items-center justify-start lg:justify-center backdrop-blur-[1px] overflow-x-auto p-4`}
    >
      <div
        data-testid={dataTestId}
        className={`${colors.bgModal} max-h-[90vh] w-[1200px] min-w-[1200px] flex-shrink-0 rounded-lg shadow-xl flex flex-col`}
      >
        {/* Header */}
        <div className={`px-6 py-4 flex-shrink-0`} data-testid={`${dataTestId}-header`}>
          <div className="flex items-center justify-between">
            <h2 className={`${colors.textPrimary} text-xl font-bold uppercase`} data-testid={`${dataTestId}-title`}>
              {t("project.notice-infor.select-verification-report-version", "Select Verification Report Version")}
            </h2>
          </div>
        </div>

        {/* Controls */}
        <div className={`flex-shrink-0 px-6 pt-4`}>
          <TableHeaderControls
            onAdvancedSearchToggle={handleAdvancedSearchToggle}
            searchField="search"
            searchValue={searchFilters.search ?? ""}
            onSearch={searchHandler}
            placeholderSearch={t("common.search-placeholder")}
            isSearching={isVerificationReportListLoading}
            isDisplayAddButton={false}
            isDisplayDeleteButton={false}
            searchInputTestId={fieldInputTestId("search")}
            advancedSearchButtonTestId={fieldInputTestId("advanced-search-toggle")}
          />
        </div>

        {/* Advanced Search */}
        {showAdvancedSearch && (
          <div className="px-6 z-[99]">
            <AdvancedSearch
              title={t("project.advancedSearch")}
              searchFields={searchFields}
              searchHandler={searchHandler}
              withoutSearch={true}
            />
          </div>
        )}

        {/* Content */}
        <div className="flex flex-1 flex-col px-6 min-h-0 overflow-y-hidden" data-testid={`${dataTestId}-table`}>
          <TableWithPagination
            columns={columns}
            data={verificationReports}
            colSize={12}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            loading={isVerificationReportListLoading}
            totalPages={totalPages}
            totalData={totalData}
            columnConfig={columnConfig}
            onRowClick={handleRowClick}
            maxRowsPerPage={itemsPerPage}
            onItemsPerPageChange={setItemsPerPage}
            showPagination={true}
            rowKey="verificationReportSeq"
            hasRadioButton={true}
            selectedRows={
              selectedReport
                ? [selectedReport.verificationReportSeq]
                : selectedVerificationReportSeq
                ? [selectedVerificationReportSeq]
                : []
            }
            onCheckboxChange={handleRadioTableChange}
            shouldShowGoToPage={true}
            showItemsPerPageSelector={true}
            scrollableBody={true}
            stickyHeader={true}
            dataTestId={`${dataTestId}-table`}
          />
        </div>

        {/* Footer - Sticky */}
        <div className={`px-6 py-4 ${colors.borderPrimary} flex-shrink-0`} data-testid={`${dataTestId}-footer`}>
          <div className="flex items-center justify-end">
            <div className="flex items-center space-x-3">
              <OutlineButton data-testid={`${dataTestId}-cancel-button`} onClick={handleClose}>{t("common.cancel-button")}</OutlineButton>
              <PrimaryButton data-testid={`${dataTestId}-confirm-button`} onClick={handleConfirm} disabled={!selectedReport}>
              {t("common.confirm", "Confirm")}
            </PrimaryButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectVerificationReportVersionModal;
